import { useState, type FormEvent, type ReactElement } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Eye, EyeOff } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { AuthApiError, login } from '@/services/authApi';

import { formatIdentifierInput, normalizeIdentifier } from './identifier';
import { storeAuthSession } from './session';

const API_BASE_URL = (import.meta.env.VITE_API_URL ?? '').trim().replace(/\/+$/, '');

export function LoginPage(): ReactElement {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  function handleUsernameChange(value: string): void {
    setUsername(formatIdentifierInput(value));
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    setIsSubmitting(true);
    try {
      const response = await login(API_BASE_URL, normalizeIdentifier(username), password);
      storeAuthSession(response.access_token, response.expires_in, response.preferred_username);
      await navigate({ to: '/collections' });
    } catch (err) {
      if (err instanceof AuthApiError && err.status === 403) {
        setError('Usuário autenticado, mas sem permissão de acesso à rotina de upload.');
      } else if (err instanceof AuthApiError && err.status === 401) {
        setError('CPF ou senha inválidos.');
      } else {
        setError('Não foi possível fazer login. Tente novamente.');
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <main className="bg-background text-foreground flex min-h-dvh items-center justify-center p-6">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle>Acesso à rotina de upload</CardTitle>
          <CardDescription>Entre com seu CPF e senha do MT LOGIN.</CardDescription>
        </CardHeader>
        <CardContent>
          <form
            className="flex flex-col gap-3"
            onSubmit={(e) => {
              void handleSubmit(e);
            }}
          >
            <Input
              autoComplete="username"
              inputMode="numeric"
              placeholder="CPF"
              value={username}
              onChange={(e) => handleUsernameChange(e.target.value)}
              disabled={isSubmitting}
              required
            />
            <div className="relative">
              <Input
                type={isPasswordVisible ? 'text' : 'password'}
                autoComplete="current-password"
                placeholder="Senha"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isSubmitting}
                required
                className="pr-8"
              />
              <button
                type="button"
                onClick={() => setIsPasswordVisible((visible) => !visible)}
                disabled={isSubmitting}
                aria-label={isPasswordVisible ? 'Ocultar senha' : 'Exibir senha'}
                className="absolute inset-y-0 right-0 flex items-center px-2 text-muted-foreground hover:text-foreground disabled:pointer-events-none disabled:opacity-50"
              >
                {isPasswordVisible ? (
                  <EyeOff className="size-4" aria-hidden="true" />
                ) : (
                  <Eye className="size-4" aria-hidden="true" />
                )}
              </button>
            </div>
            {error && <p className="text-destructive text-sm">{error}</p>}
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Entrando...' : 'Entrar'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </main>
  );
}
